import { GroupRoleRequest, updateGroupMemberRole } from '@front-app/services/GroupsApi/GroupsMemberApi';
import { GroupsStore } from '@front-app/store/singletons/Groups';
import { ref, Ref } from '@vue/composition-api';
import { isAxiosError } from '@front-app/util/axios';

interface UseGroupMemberStateChange {
	isChangingState: Ref<boolean>;
	isErrorJoining: Ref<boolean>,
	request(groupId: string, targetRole: GroupRoleRequest, inviteId?: string, accessCode?: string): Promise<void>;
}

const isChangingState = ref(false);
const isErrorJoining = ref(false);

/**
 * A hook that allows updating a user's state for a group. The loading state is shared
 * globally throughout all instances of this hook.
 */
export const useGroupMemberStateChange = (): UseGroupMemberStateChange => ({
	isChangingState,
	isErrorJoining,
	async request(groupId: string, targetRole: GroupRoleRequest, inviteId?: string, accessCode?: string): Promise<void> {
		// eslint-disable-next-line no-param-reassign
		isChangingState.value = true;
		try {
			// Provide access code if it is in the Group Join Requirements
			if (accessCode) {
				await updateGroupMemberRole(groupId, targetRole, inviteId, accessCode);
			} else {
				await updateGroupMemberRole(groupId, targetRole, inviteId);
			}
		} catch (e: unknown) {
			// If we failed, it might be because the inviteId isn't linked to the current user,
			// so we can retry without it
			if (inviteId) {
				await updateGroupMemberRole(groupId, targetRole);
			} else if (isAxiosError(e) && e.response?.status === 400) {
				isChangingState.value = false;
				isErrorJoining.value = true;
			} else {
				isChangingState.value = false;
				throw e;
			}
		}
		// refetch the groups to update stale data
		await GroupsStore.getInstance().fetchGroups();
		// eslint-disable-next-line no-param-reassign
		isChangingState.value = false;
	},
});
